import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { UserState } from '@shared/states/user.state';
import { Authority } from '@shared/types/authority';

/**
 * Restricts /copilot matching to users the TENANT's own entitlement state admits:
 *  - holds ANY of the route's AI authorities (COPILOT_ENTRY_AUTHORITIES), OR
 *  - has early access to one of them, OR
 *  - is on the upsell path (subscription needed and the AI module is a sellable,
 *    currently disabled module) — the same condition the navbar uses for the
 *    Copilot menu item, so those users still reach the subscription dialog.
 *
 * Unlike the hard AuthoritiesCanMatchGuard this preserves the early-access and
 * upsell paths (#305 -> #307). A tenant whose sellable-module catalog has no AI
 * module, and whose users hold no AI authority, still does not match the route —
 * that is the hole SubscriptionCanMatchGuard's "no catalog entry ⇒ allow"
 * fallback leaves open, and closing it is this guard's whole job.
 *
 * NO CONTOUR LIST. This used to ALSO require the deployment's SiteName to appear
 * in a hardcoded COPILOT_ENABLED_SITES = [Reluna, Preprod, Lwam] (RLN2-708). That
 * made buying Copilot insufficient: a new customer's site was not in the list, so
 * /copilot did not route until an engineer edited the frontend and cut a webapp
 * release. The tenant's REAL entitlement state is already loaded and already
 * consulted here — `UserState.authorities$` (granted rights) and
 * `UserState.disabledAuthorities$` (the sellable-module catalog behind
 * needSubscription/hasDisabledAuthorities/hasEarlyAccess), the same mechanism
 * screener / risks / marketplace gate on — so the contour list added nothing a
 * runtime signal did not already say, and subtracted onboarding.
 */
export const CopilotCanMatchGuard: CanMatchFn = (route) => {
  const userState = inject(UserState);
  const authorities = route.data?.authorities as Authority[];

  if (userState.hasAuthorities(authorities, 'one_of')) {
    return true;
  }

  if (userState.hasEarlyAccess(authorities, 'one_of')) {
    return true;
  }

  return userState.needSubscription() && userState.hasDisabledAuthorities(authorities, 'one_of');
};
